/**
 * Colonnes d'une liste de parcours candidats (portail admin).
 *
 * Écrites contre la projection de `listerParcoursSuperAdmin`. Utilisées par la
 * vue « hors campagne » ; la liste des parcours D'UNE campagne, elle, passe par
 * le composant partagé `@repo/campagne`.
 */
import {
  colonnesEpreuves,
  FACETTE_EPREUVES,
  type ResumeEpreuve,
} from "@repo/campagne";
import {
  Progress,
  StatutParcoursBadge,
  filtreFacette,
  filtreRecherche,
  triOrdonne,
  type ColonneDataTable,
  type FacetteFiltre,
} from "@repo/ui";
import { dateCourte } from "@repo/utils";
import { Mail } from "lucide-react";
import type { Id } from "@convex/_generated/dataModel";

const ORDRE_STATUT = ["en_cours", "termine", "archive"] as const;

const LIBELLES_STATUT: Record<string, string> = {
  en_cours: "En cours",
  termine: "Terminé",
  archive: "Archivé",
};

const LIBELLES_LIEN: Record<string, string> = {
  envoye: "Envoyé",
  non_envoye: "Non envoyé",
};

/** Ordre métier : ce qui reste à faire d'abord. */
const ORDRE_LIEN = ["non_envoye", "envoye"] as const;

/** Mêmes facettes que la table partagée `@repo/campagne` : même métier. */
export const FACETTES_PARCOURS_ADMIN: FacetteFiltre[] = [
  {
    colonne: "statut",
    libelle: "Statut",
    libelles: LIBELLES_STATUT,
    ordre: ORDRE_STATUT,
  },
  {
    colonne: "lien",
    libelle: "Lien",
    libelles: LIBELLES_LIEN,
    ordre: ORDRE_LIEN,
  },
  FACETTE_EPREUVES,
];

/** Recherche de la table des parcours admin, à passer à `barreFiltres`. */
export const RECHERCHE_PARCOURS_ADMIN = {
  colonne: "candidat",
  placeholder: "Nom, prénom, email…",
  libelle: "Rechercher un candidat",
} as const;

/** Projection retournée par `listerParcoursSuperAdmin`. */
export type ParcoursAdminLigne = {
  _id: Id<"parcoursEvaluations">;
  nomCandidat?: string;
  prenomCandidat?: string;
  emailCandidat?: string;
  anonyme?: boolean;
  statutCache?: string;
  dateCreation: number;
  organisationNom: string | null;
  campagneNom: string | null;
  progression: { total: number; terminees: number; pourcentage: number };
  lienEnvoye?: boolean;
  evaluationsResume: ResumeEpreuve[];
};

/** Nom affichable d'un candidat, anonymat respecté. */
function libelleCandidat(p: ParcoursAdminLigne): string {
  if (p.anonyme) return "Candidat anonyme";
  const nom = [p.prenomCandidat, p.nomCandidat].filter(Boolean).join(" ");
  return nom || p.emailCandidat || "Candidat";
}

export function colonnesParcoursAdmin(): ColonneDataTable<ParcoursAdminLigne>[] {
  return [
    {
      id: "candidat",
      header: "Candidat",
      accessorFn: (p) => libelleCandidat(p),
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="font-medium truncate">
            {libelleCandidat(row.original)}
          </div>
          {!row.original.anonyme && row.original.emailCandidat && (
            <div className="text-sm text-muted-foreground truncate">
              {row.original.emailCandidat}
            </div>
          )}
        </div>
      ),
      enableHiding: false,
      // La recherche porte sur cette colonne mais lit prénom, nom ET email :
      // taper un email trouve la ligne même si le candidat est anonyme dans
      // l'affichage. Un parcours anonyme reste cherchable par son email, qui
      // est la seule prise que l'admin a dessus.
      filterFn: filtreRecherche<ParcoursAdminLigne>((p) => [
        p.prenomCandidat,
        p.nomCandidat,
        p.emailCandidat,
      ]),
      meta: { libelle: "Candidat", largeur: "26%" },
    },
    {
      id: "lien",
      header: "Lien",
      // Clé métier, pas un booléen numérique : c'est elle que la facette
      // affiche et que l'URL transporte. L'ordre passe par `sortFn`.
      accessorFn: (p) => (p.lienEnvoye ? "envoye" : "non_envoye"),
      filterFn: filtreFacette<ParcoursAdminLigne>(),
      sortFn: triOrdonne<ParcoursAdminLigne>(ORDRE_LIEN),
      cell: ({ row }) =>
        row.original.lienEnvoye ? (
          <Mail className="h-4 w-4 text-etat-fait" aria-label="Lien envoyé" />
        ) : (
          <Mail
            className="h-4 w-4 text-muted-foreground/30"
            aria-label="Lien non envoyé"
          />
        ),
      meta: { libelle: "Lien", largeur: "7%" },
    },
    ...colonnesEpreuves<ParcoursAdminLigne>((p) => p.evaluationsResume),
    {
      id: "progression",
      header: "Progression",
      accessorFn: (p) => p.progression.pourcentage,
      cell: ({ row }) => {
        const p = row.original.progression;
        return (
          <div className="min-w-[100px]">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="data">{p.pourcentage}%</span>
              <span className="data text-muted-foreground">
                {p.terminees}/{p.total}
              </span>
            </div>
            <Progress value={p.pourcentage} className="h-2" />
          </div>
        );
      },
      meta: { libelle: "Progression", largeur: "18%" },
    },
    {
      id: "statut",
      header: "Statut",
      accessorFn: (p) => p.statutCache ?? "",
      filterFn: filtreFacette<ParcoursAdminLigne>(),
      sortFn: triOrdonne<ParcoursAdminLigne>(ORDRE_STATUT),
      cell: ({ row }) => (
        <StatutParcoursBadge statut={row.original.statutCache} />
      ),
      meta: { libelle: "Statut", largeur: "14%" },
    },
    {
      id: "dateCreation",
      header: "Créé le",
      accessorFn: (p) => p.dateCreation,
      cell: ({ row }) => (
        <span className="data text-sm">
          {dateCourte(row.original.dateCreation)}
        </span>
      ),
      meta: { libelle: "Créé le", largeur: "12%" },
    },
  ];
}
